import React, { FC } from 'react';
import PollEntity from '../../../../store/polls-store/poll-entity';
import HideForPresenter from '../../shared/hide-for-presenter';
import {
  DEFAULT_STREAM_ADJUSTMENT,
  PollsTabsOnMobile,
  PollsViewStatuses,
  FullScreenButtonContent
} from '../../../../constans/polls-constants';
import OpenCloseButton from '../../shared/open-close-button';
import PollStatus from '../../shared/poll-status/poll-status';
import { observer } from 'mobx-react';
import ClosingProgress from '../../shared/closing-progress';
import classNames from 'classnames';
import ElipsisMenuWrapper from '../../shared/elipsis-menu-wrapper';
import FullScreenButton from '../../shared/full-screen-button';
import ResultSlideButton from '../../shared/result-slide-button';
import { getMixpanelService } from '../../../../utils/services/mixpanel-service-instance';
import { MIXPANEL_EVENT_IDENTIFIERS, MIXPANEL_EVENT_TYPES } from '../../../../constans/mixpanel-contants';
import { isSafari } from '../../../../constans/url-constants';

interface IProps {
  poll: PollEntity;
  changePollStatus: (poll: PollEntity, customStatus?: PollsViewStatuses) => Promise<void>;
  isEllipsisMenuOpened: boolean;
  setShouldShowEllipsisMenu: (value: boolean) => void;
  setShouldShowDeleteModal: (value: boolean) => void;
  setShouldShowResetModal: (value: boolean) => void;
  setShouldShowFullScreen: (value: boolean) => void;
  setShouldShowAddResultsSlideModal: (value: boolean) => void;
  toggleManagePollModal: () => void;
  setActiveMobileTab: (tab: PollsTabsOnMobile) => void;
  isMobile: boolean;
  isOnePollOpened: boolean;
  setIsFullScreenModeSafari: (value: boolean) => void;
}

const PollDetailsHeader: FC<IProps> = ({
  poll,
  changePollStatus,
  isEllipsisMenuOpened,
  setShouldShowEllipsisMenu,
  setShouldShowDeleteModal,
  setShouldShowResetModal,
  setShouldShowAddResultsSlideModal,
  toggleManagePollModal,
  setShouldShowFullScreen,
  setActiveMobileTab,
  isMobile,
  isOnePollOpened,
  setIsFullScreenModeSafari
}) => {
  const status = poll.status || PollsViewStatuses.NOT_STARTED;
  const shouldShowOpenCloseBtn = status !== PollsViewStatuses.COMPLETED && status !== PollsViewStatuses.RESETED;
  const isOpenButtonDisabled = isOnePollOpened && status === PollsViewStatuses.NOT_STARTED;
  const shouldShowResultSlideBtn = status === PollsViewStatuses.COMPLETED && !poll.shouldShowClosingProgress;

  const changePollStatusHandler = (): void => {
    changePollStatus(poll);
  };

  const openInNewFullScreenWindow = (): void => {
    if (isSafari) {
      setIsFullScreenModeSafari(true);
    }

    setShouldShowFullScreen(true);
    getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.POLLS, MIXPANEL_EVENT_TYPES.FULLSCREEN);
  };

  return (
    <div className='poll-details-header'>
      <div className='poll-details-header-info'>
        <div className='poll-details-header-presenter'>
          <HideForPresenter hideForPresenter={poll.hideForPresenter}/>
          {isMobile &&
            <ElipsisMenuWrapper
              poll={poll}
              isEllipsisMenuOpened={isEllipsisMenuOpened}
              setShouldShowEllipsisMenu={setShouldShowEllipsisMenu}
              setShouldShowDeleteModal={setShouldShowDeleteModal}
              setShouldShowResetModal={setShouldShowResetModal}
              toggleManagePollModal={toggleManagePollModal}
              setActiveMobileTab={setActiveMobileTab}
              changePollStatus={changePollStatus}
              isMobile={isMobile}
              isOnePollOpened={isOnePollOpened}
            />
          }
        </div>
        <div className='poll-details-header-question'>{poll.value}</div>
        <div
          className={classNames('status-votes-container', {
            'status-votes-container_reversed': poll.shouldShowClosingProgress
          })}
        >
          <div className='status-votes-container-info'>
            {poll.shouldShowClosingProgress
              ? <ClosingProgress delay={DEFAULT_STREAM_ADJUSTMENT} timestamp={poll.pollStatusTimestamp} handleFinishClosing={poll.hideClosingProgress}/>
              : <PollStatus status={status} additionalClassName='poll-details-header-status'/>
            }
            {status !== PollsViewStatuses.NOT_STARTED && <span className='votes'>{poll.countVotes} Votes</span>}
          </div>
          {shouldShowOpenCloseBtn && isMobile && <OpenCloseButton onClick={changePollStatusHandler} status={status}/>}
        </div>
      </div>
      <div className='poll-details-header-actions'>
        {shouldShowOpenCloseBtn && !isMobile && <OpenCloseButton disabled={isOpenButtonDisabled} onClick={changePollStatusHandler} status={status}/>}
        {shouldShowResultSlideBtn && (
          <ResultSlideButton
            openConfirmationModal={setShouldShowAddResultsSlideModal}
            showButton={!poll.result_slide_added}
            isLoading={poll.shouldShowGenerating}
          />
        )}
        {!poll.shouldShowClosingProgress &&
          <FullScreenButton buttonStateContent={FullScreenButtonContent.OPEN} actionHandler={openInNewFullScreenWindow}/>
        }
      </div>
    </div>
  );
};

export default observer(PollDetailsHeader);
